import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchInvitations, fetchMembershipTypes, createInvitation } from '../api';

// Admin-only: issue and review invitations.
//
// Only rendered for admins (gated in Profile). Creating an invite returns a
// single-use acceptance link the admin shares with the invitee. Email delivery
// (SendGrid) comes later; for now the admin copies the link.
export default function AdminInvites() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [invites, setInvites] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ email: '', membershipTypeId: '', role: 'member', expiresInDays: 14 });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // { acceptUrl, email } on success
  const [feedback, setFeedback] = useState(null); // { kind, message }

  async function loadAll() {
    const [invRes, typeRes] = await Promise.all([
      fetchInvitations(instance, account),
      fetchMembershipTypes(instance, account),
    ]);
    if (invRes.ok) setInvites(invRes.data.invitations || []);
    else setError(`Couldn't load invitations (${invRes.status})`);
    if (typeRes.ok) setTypes(typeRes.data.membershipTypes || []);
    setLoading(false);
  }

  useEffect(() => {
    if (!accountKey) return;
    let cancelled = false;
    (async () => {
      try { if (!cancelled) await loadAll(); }
      catch (e) { if (!cancelled) { setError(e.message); setLoading(false); } }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  async function handleSubmit(e) {
    e.preventDefault();
    setFeedback(null);
    setResult(null);
    const email = form.email.trim();
    if (!email) { setFeedback({ kind: 'err', message: 'Enter an email address.' }); return; }

    setSubmitting(true);
    try {
      const { ok, data } = await createInvitation(instance, account, {
        email,
        membershipTypeId: form.membershipTypeId || undefined,
        role: form.role || undefined,
        expiresInDays: Number(form.expiresInDays) || undefined,
      });
      if (ok) {
        setResult({ acceptUrl: data.acceptUrl, email: data.email, token: data.token, emailed: data.emailed, emailConfigured: data.emailConfigured });
        setFeedback({
          kind: 'ok',
          message: data.emailed
            ? `Invitation emailed to ${data.email}${data.role === 'admin' ? ' as an admin' : ''}.`
            : `Invitation created for ${data.email}, but the email could not be sent — share the link below.`,
        });
        setForm({ email: '', membershipTypeId: '', role: 'member', expiresInDays: 14 });
        await loadAll();
      } else {
        setFeedback({ kind: 'err', message: data?.message || 'Could not create invitation.' });
      }
    } catch (err) {
      setFeedback({ kind: 'err', message: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  function copyLink(url) {
    if (!url) return;
    navigator.clipboard?.writeText(url).then(
      () => setFeedback({ kind: 'ok', message: 'Link copied to clipboard.' }),
      () => setFeedback({ kind: 'err', message: 'Copy failed — select and copy manually.' }),
    );
  }

  if (loading) return <div className="card">Loading invitations…</div>;
  if (error) return <div className="card error"><h2>Invitations unavailable</h2><p>{error}</p></div>;

  return (
    <div className="card">
      <h2>Invite a member</h2>
      <form className="booking-form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Email</span>
          <input type="email" value={form.email} placeholder="person@example.com"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </label>
        <label className="field">
          <span>Role</span>
          <select value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
            <option value="member">Member</option>
            <option value="admin">Admin (can invite others)</option>
          </select>
        </label>
        <label className="field">
          <span>Tier (optional)</span>
          <select value={form.membershipTypeId}
            onChange={(e) => setForm((f) => ({ ...f, membershipTypeId: e.target.value }))}>
            <option value="">— none —</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — ${(t.price_cents / 100).toFixed(0)}/{t.billing_period === 'yearly' ? 'yr' : 'mo'}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Expires (days)</span>
          <input type="number" min="1" max="90" value={form.expiresInDays}
            onChange={(e) => setForm((f) => ({ ...f, expiresInDays: e.target.value }))} />
        </label>
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create invite'}
        </button>
      </form>

      {feedback && <p className={feedback.kind === 'ok' ? 'notice' : 'error-inline'}>{feedback.message}</p>}

      {result?.acceptUrl && (
        <div className="notice">
          {result.emailed ? (
            <p><strong>✉️ Invitation emailed to {result.email}.</strong> You can also copy the link below as a backup.</p>
          ) : (
            <p><strong>Share this link with {result.email}:</strong> {result.emailConfigured === false
              ? '(email isn\'t configured yet)'
              : '(the email couldn\'t be sent this time)'}</p>
          )}
          <code className="ext-id">{result.acceptUrl}</code>
          <p><button onClick={() => copyLink(result.acceptUrl)}>Copy link</button></p>
          <p className="muted">The link is single-use and expires.</p>
        </div>
      )}

      <h3>Issued invitations</h3>
      {invites.length === 0 ? (
        <p className="muted">No invitations yet.</p>
      ) : (
        <ul className="res-list">
          {invites.map((i) => (
            <li key={i.id}>
              <strong>{i.email}</strong> — {i.status}
              {i.assigned_role === 'admin' ? ' · 🛡️ admin' : ''}
              {i.membership_type_name ? ` · ${i.membership_type_name}` : ''}
              {' · '}<span className="muted">expires {String(i.expires_at).slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
